import { getCurrentUser } from '@/services/api/auth';
import type { RotinaGrupo, UltimaExecucao } from '@/services/api/rotinas';

export const DIAS_SEMANA = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
export const DIAS_CURTOS = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
/** Como o backend escreve o dia nos avisos do import ("16/09 é quarta"). */
export const DIAS_MINUSCULOS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];

/** A materialização roda todo dia nesta hora de Brasília e cria os broadcasts do dia SEGUINTE. */
export const HORA_MATERIALIZACAO = '20:00';

export const hhmm = (hora: string) => hora.slice(0, 5);

export const ddmm = (d: Date) =>
    `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Hoje no calendário de Brasília, como Date local à meia-noite. */
export const hojeBRT = () =>
    new Date(new Intl.DateTimeFormat('en-US', { timeZone: 'America/Sao_Paulo' }).format(new Date()));

export const horaAgoraBRT = () =>
    new Intl.DateTimeFormat('en-GB', {
        timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit', hour12: false,
    }).format(new Date());

/**
 * O dia que a próxima materialização vai criar. Antes das 20h é amanhã; depois,
 * amanhã já nasceu e o próximo é depois de amanhã. É ele que decide qual Rotina roda.
 */
export const proximaMaterializacao = (): Date => {
    const d = hojeBRT();
    d.setDate(d.getDate() + (horaAgoraBRT() >= HORA_MATERIALIZACAO ? 2 : 1));
    return d;
};

/** "hoje", "amanhã" ou "qua 08/10" — o dia como o gestor fala. */
export const diaFalado = (d: Date) => {
    const dias = Math.round((d.getTime() - hojeBRT().getTime()) / 86_400_000);
    if (dias === 0) return 'hoje';
    if (dias === 1) return 'amanhã';
    return `${DIAS_CURTOS[d.getDay()]} ${ddmm(d)}`;
};

/** "2026-10-08" → Date local à meia-noite, comparável com `hojeBRT()`. */
export const dataLocal = (iso: string) => {
    const [a, m, d] = iso.split('-').map(Number);
    return new Date(a, m - 1, d);
};

/** Próxima data em que este dia da semana cai, em BRT — é a data que o título vai anunciar. */
export const proximaData = (diaSemana: number) => {
    const d = hojeBRT();
    d.setDate(d.getDate() + ((diaSemana - d.getDay() + 7) % 7));
    return ddmm(d);
};

/**
 * O título que vai SAIR, como `montar_titulo` do backend: data e hora acrescentadas pelo
 * sistema, hora sem zero à esquerda. Nunca o molde cru.
 */
export const tituloDoGrupo = (g: RotinaGrupo, diaSemana: number) => {
    if (g.titulo_com_data_hora === 0) return g.titulo_modelo;
    const [h, m] = hhmm(g.hora_local).split(':');
    return `${g.titulo_modelo} ${proximaData(diaSemana)} (${Number(h)}:${m})`;
};

export const CAMPOS_PROXIMA: Record<string, string> = {
    utility_template_id: 'template utility',
    secondary_utility_template_id: 'template utility secundário',
    translated_template: 'tradução por idioma',
    ai_translate: 'tradução por IA',
    source_language: 'idioma de origem',
    target_language: 'idioma de destino',
    app_id: 'app',
    ativa: 'ativar/desativar o grupo',
    paginas: 'lista de páginas',
};

export const STATUS_EXECUCAO: Record<UltimaExecucao['status'], string> = {
    executando: 'em andamento',
    ok: 'concluída',
    parcial: 'parcial',
    falha: 'falhou',
};

/**
 * Quantos idiomas o grupo tem guardados. `translated_template` é o mapa
 * `{idioma: texto}` que o import gravou; vem como objeto ou como texto JSON.
 */
export const idiomasDoGrupo = (tt: RotinaGrupo['translated_template']): string[] => {
    let obj: unknown = tt;
    if (typeof tt === 'string') {
        try { obj = JSON.parse(tt); } catch { return []; }
    }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];
    return Object.keys(obj as Record<string, unknown>);
};

export type EstadoTraducao =
    | { tipo: 'idiomas'; idiomas: string[] }
    | { tipo: 'ia'; origem: string | null }
    /** Utility/Esteira: o texto já vem traduzido e aprovado no template, por página. */
    | { tipo: 'utility' }
    | { tipo: 'sem' };

/**
 * O que a materialização vai conseguir fazer com o texto deste grupo. Mapa
 * guardado é o caminho normal; `ai_translate` sem mapa traduz na hora; nenhum
 * dos dois significa que só o idioma-fonte vai sair.
 */
export const traducaoDoGrupo = (g: RotinaGrupo): EstadoTraducao => {
    // Utility e Esteira não traduzem: o texto vive no template aprovado por
    // página. "Sem tradução" em âmbar os acusava de um defeito que não têm, e
    // a materialização — que pula a tradução deles — desmentiria o aviso.
    if (g.utility_template_id || g.utility_esteira_id) return { tipo: 'utility' };
    const idiomas = idiomasDoGrupo(g.translated_template);
    if (idiomas.length > 0) return { tipo: 'idiomas', idiomas };
    if (g.ai_translate) return { tipo: 'ia', origem: g.source_language };
    return { tipo: 'sem' };
};

/**
 * A hora que a tela mostra: Brasília, igual ao calendário e à tela de
 * broadcasts. Cai na hora local enquanto o grupo não tem página — aí o
 * backend não tem fuso nenhum para converter.
 */
export const horaExibida = (g: RotinaGrupo) => g.hora_brt ?? hhmm(g.hora_local);

/** Ordem estável: hora e, no empate, id. A hora é o que distingue um grupo do outro. */
export const ordenarGrupos = (grupos: RotinaGrupo[]) =>
    [...grupos].sort((a, b) => horaExibida(a).localeCompare(horaExibida(b)) || a.id - b.id);

/** true quando todos os grupos usam o mesmo título: aí ele vive no cabeçalho. */
export const temTituloComum = (grupos: RotinaGrupo[]) =>
    grupos.length > 0
    && new Set(grupos.map((g) => `${g.titulo_com_data_hora}|${g.titulo_modelo}`)).size === 1;

/** Posição do horário na régua de 24h, em %. */
export const posicaoNoDia = (hora: string) => {
    const [h, m] = hhmm(hora).split(':').map(Number);
    return ((h * 60 + m) / 1440) * 100;
};

/**
 * Admin vê os broadcasts de todos, mas a importação de um dia e a rotina
 * criada são dele: os diálogos que montam Rotina a partir de broadcast pedem
 * só os do próprio admin. Para os demais, o escopo de sempre (undefined).
 */
export const meuIdSeAdmin = (): number | undefined => {
    const u = getCurrentUser();
    return u?.role === 'admin' ? u.id : undefined;
};
